import React, { useEffect, useRef, useState } from "react";
import { Loader2, Save, Mail, Phone, MapPin, User, Camera, Trash2, LockKeyhole } from "lucide-react";
import { toast } from "sonner";
import customerApi from "./customerApi";
import { errMsg } from "./hqUtils";

const AUBERGINE  = "var(--hq-deep, #5A4565)";
const PLUM       = "var(--hq-accent, #6F4A7A)";
const GOLD       = "#C9A268";
const PARCH_EDGE = "#EFE4CC";

export default function HQProfile() {
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", address_line1: "", address_line2: "", suburb: "", city: "", postcode: "", region: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [password, setPassword] = useState({ current: "", next: "", confirm: "" });
  const [changingPassword, setChangingPassword] = useState(false);
  const fileRef = useRef(null);

  const pickPhoto = () => fileRef.current?.click();

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const dataUrl = await resizeImage(file, 512);
      const r = await customerApi.post("/customer/profile-picture", {
        image_base64: dataUrl.split(",")[1],
        mime: "image/jpeg",
      });
      setProfile((p) => ({ ...p, profile_picture: r.data.profile_picture }));
      toast.success("Looking lovely! Photo saved.");
    } catch (err) {
      toast.error(errMsg(err, "Couldn't upload that photo."));
    } finally { setUploading(false); }
  };

  const removePhoto = async () => {
    setUploading(true);
    try {
      await customerApi.delete("/customer/profile-picture");
      setProfile((p) => ({ ...p, profile_picture: null }));
      toast.success("Photo removed.");
    } catch (err) {
      toast.error(errMsg(err, "Couldn't remove the photo."));
    } finally { setUploading(false); }
  };

  useEffect(() => {
    customerApi.get("/customer/me").then((r) => {
      const me = r.data || {};
      setProfile(me);
      const a = me.address || {};
      setForm({
        name: me.name || "",
        phone: me.phone || "",
        address_line1: a.line1 || me.address_line1 || "",
        address_line2: a.line2 || me.address_line2 || "",
        suburb: a.suburb || me.suburb || "",
        city: a.city || me.city || "",
        postcode: a.postcode || me.postcode || "",
        region: a.region || me.region || "",
      });
    }).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await customerApi.patch("/customer/me", {
        name: form.name,
        phone: form.phone,
        address: {
          line1: form.address_line1, line2: form.address_line2,
          suburb: form.suburb, city: form.city,
          postcode: form.postcode, region: form.region,
        },
      });
      toast.success("Profile saved.");
    } catch (e) {
      toast.error(errMsg(e, "Couldn't save."));
    } finally { setSaving(false); }
  };

  const changePassword = async () => {
    if (password.next.length < 8) return toast.error("New password must be at least 8 characters.");
    if (password.next !== password.confirm) return toast.error("New passwords do not match.");
    setChangingPassword(true);
    try {
      await customerApi.post("/customer/change-password", { current_password: password.current, new_password: password.next });
      setPassword({ current: "", next: "", confirm: "" });
      toast.success("Password changed securely.");
    } catch (error) { toast.error(errMsg(error, "Couldn't change your password.")); }
    finally { setChangingPassword(false); }
  };

  if (loading) return <div className="opacity-70"><Loader2 className="w-4 h-4 inline animate-spin mr-2" />Loading…</div>;

  return (
    <div data-testid="hq-profile" className="space-y-5" style={{ color: AUBERGINE }}>
      <header>
        <div className="text-[10px] tracking-[0.45em] uppercase font-semibold" style={{ color: PLUM }}>✦ Heritage Headquarters</div>
        <h1 className="text-4xl mt-1.5">My Profile</h1>
        <p className="italic opacity-75 text-sm mt-1">Update your contact details and shipping address.</p>
      </header>

      {/* Photo card , the fun bit */}
      <div
        className="rounded p-5 flex flex-col sm:flex-row sm:items-center gap-5 border"
        style={{ borderColor: PARCH_EDGE, background: "linear-gradient(135deg, #FBF6E9 0%, #F4EBF3 100%)" }}
        data-testid="hq-profile-photo-card"
      >
        <div
          className="w-28 h-28 rounded-full flex-shrink-0 overflow-hidden shadow-lg mx-auto sm:mx-0"
          style={{ border: `3px solid ${GOLD}`, backgroundColor: "#FBF6E9" }}
        >
          <img
            src={profile?.profile_picture || "/photos/hq/hq-seal-purple.png"}
            alt="Profile"
            data-testid="hq-profile-photo"
            className={profile?.profile_picture ? "w-full h-full object-cover" : "w-full h-full object-contain p-2"}
          />
        </div>
        <div className="text-center sm:text-left flex-1">
          <div className="text-[10px] tracking-[0.3em] uppercase font-semibold" style={{ color: PLUM }}>Your Photo</div>
          <p className="text-sm italic opacity-75 mt-1">
            Add a photo of yourself, or your proudest dahlia, to make Heritage HQ feel like home.
          </p>
          <div className="flex flex-wrap justify-center sm:justify-start gap-2 mt-3">
            <button
              type="button"
              onClick={pickPhoto}
              disabled={uploading}
              data-testid="hq-profile-photo-upload"
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-[10px] tracking-[0.2em] uppercase font-semibold"
              style={{ backgroundColor: AUBERGINE, color: "#FBF6E9" }}
            >
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
              {profile?.profile_picture ? "Change photo" : "Add a photo"}
            </button>
            {profile?.profile_picture && (
              <button
                type="button"
                onClick={removePhoto}
                disabled={uploading}
                data-testid="hq-profile-photo-remove"
                className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-[10px] tracking-[0.2em] uppercase font-semibold border"
                style={{ borderColor: AUBERGINE, color: AUBERGINE }}
              >
                <Trash2 className="w-3.5 h-3.5" /> Remove
              </button>
            )}
          </div>
          <div className="flex flex-wrap justify-center sm:justify-start gap-2 mt-3.5">
            {profile?.customer_id && <Badge text={`Heritage ID ${profile.customer_id}`} />}
            {String(profile?.created_at || "").slice(0, 4) && <Badge text={`Member since ${String(profile.created_at).slice(0, 4)}`} />}
            {Number(profile?.heritage_credits || 0) > 0 && <Badge text={`NZ$${Number(profile.heritage_credits).toFixed(2)} credit`} gold />}
          </div>
        </div>
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onPhoto} data-testid="hq-profile-photo-input" />
      </div>

      <div className="bg-white border rounded p-5 space-y-3" style={{ borderColor: PARCH_EDGE }}>
        <Row icon={Mail} label="Email">
          <div className="text-sm">{profile?.email}</div>
          <div className="text-[10px] opacity-60 italic">Contact us if you need to change your email.</div>
        </Row>
        <Row icon={User} label="Name">
          <Input value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="hq-profile-name" />
        </Row>
        <Row icon={Phone} label="Phone">
          <Input value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="hq-profile-phone" />
        </Row>
        <Row icon={MapPin} label="Address">
          <div className="space-y-2">
            <Input placeholder="Line 1" value={form.address_line1} onChange={(v) => setForm({ ...form, address_line1: v })} testid="hq-profile-line1" />
            <Input placeholder="Line 2" value={form.address_line2} onChange={(v) => setForm({ ...form, address_line2: v })} testid="hq-profile-line2" />
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="Suburb" value={form.suburb} onChange={(v) => setForm({ ...form, suburb: v })} testid="hq-profile-suburb" />
              <Input placeholder="City" value={form.city} onChange={(v) => setForm({ ...form, city: v })} testid="hq-profile-city" />
              <Input placeholder="Postcode" value={form.postcode} onChange={(v) => setForm({ ...form, postcode: v })} testid="hq-profile-postcode" />
              <Input placeholder="Region" value={form.region} onChange={(v) => setForm({ ...form, region: v })} testid="hq-profile-region" />
            </div>
          </div>
        </Row>
      </div>

      <button
        type="button"
        onClick={save}
        disabled={saving}
        data-testid="hq-profile-save"
        className="inline-flex items-center gap-2 px-4 py-2 rounded text-xs tracking-[0.2em] uppercase font-semibold"
        style={{ backgroundColor: AUBERGINE, color: "#FBF6E9" }}
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
        Save changes
      </button>

      <section className="bg-white border rounded p-5" style={{ borderColor: PARCH_EDGE }} data-testid="hq-change-password">
        <div className="flex items-center gap-2"><LockKeyhole className="w-5 h-5" style={{ color: PLUM }} /><div><div className="text-[10px] tracking-[0.3em] uppercase font-semibold" style={{ color: PLUM }}>Security</div><h2 className="font-serif text-2xl">Change Password</h2></div></div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          <label className="text-xs"><span className="block mb-1">Current password</span><input type="password" value={password.current} onChange={(event) => setPassword({ ...password, current: event.target.value })} data-testid="hq-password-current" className="w-full h-10 px-3 border rounded" /></label>
          <label className="text-xs"><span className="block mb-1">New password</span><input type="password" minLength={8} value={password.next} onChange={(event) => setPassword({ ...password, next: event.target.value })} data-testid="hq-password-new" className="w-full h-10 px-3 border rounded" /></label>
          <label className="text-xs"><span className="block mb-1">Confirm new password</span><input type="password" minLength={8} value={password.confirm} onChange={(event) => setPassword({ ...password, confirm: event.target.value })} data-testid="hq-password-confirm" className="w-full h-10 px-3 border rounded" /></label>
        </div>
        <button type="button" onClick={changePassword} disabled={changingPassword || !password.current || !password.next} data-testid="hq-password-submit" className="mt-4 h-10 px-4 rounded text-white text-xs tracking-[0.18em] uppercase font-semibold disabled:opacity-50" style={{ backgroundColor: AUBERGINE }}>{changingPassword ? "Changing…" : "Change password"}</button>
      </section>
    </div>
  );
}

function Badge({ text, gold }) {
  return (
    <span
      className="inline-block px-3 py-1 rounded-full text-[9px] tracking-[0.25em] uppercase font-semibold border"
      style={gold
        ? { backgroundColor: "#FBF3DF", color: "#8A6A2F", borderColor: GOLD }
        : { backgroundColor: "var(--hq-soft, #F2EAF3)", color: PLUM, borderColor: "var(--hq-soft-edge, #E1D3E4)" }}
    >
      {text}
    </span>
  );
}

function resizeImage(file, maxDim) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL("image/jpeg", 0.85));
      URL.revokeObjectURL(img.src);
    };
    img.onerror = () => reject(new Error("Could not read that image."));
    img.src = URL.createObjectURL(file);
  });
}

function Row({ icon: Icon, label, children }) {
  return (
    <div className="grid grid-cols-[24px_1fr] sm:grid-cols-[24px_120px_1fr] gap-3 items-start min-w-0">
      <Icon className="w-4 h-4 mt-2 opacity-60" style={{ color: PLUM }} />
      <div className="text-[10px] tracking-[0.3em] uppercase font-semibold mt-2 opacity-70">{label}</div>
      <div className="min-w-0 col-span-2 sm:col-span-1">{children}</div>
    </div>
  );
}

function Input({ value, onChange, placeholder, testid }) {
  return (
    <input
      value={value || ""}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="w-full px-3 py-2 text-sm border rounded outline-none focus:border-[#5B2A4F]"
      style={{ borderColor: PARCH_EDGE, color: AUBERGINE }}
    />
  );
}
